import { useEffect, useRef } from 'react';

import { UserSession } from '@/api/client';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import { readTenantSlugFromCurrentUrl } from '@/router/paths';
import { resolveSlugToTenantId } from '@/router/tenant-routing';

function resolveActiveTenantId(user: UserSession): string | null {
  const urlTenantSlug = readTenantSlugFromCurrentUrl();
  if (urlTenantSlug) {
    return (
      resolveSlugToTenantId(urlTenantSlug, user.saas_tenants ?? undefined) ??
      user.saas_tenant_id ??
      null
    );
  }
  return user.saas_tenant_id ?? null;
}

/**
 * Hook to handle analytics tracking for user authentication state changes
 *
 * This hook manages analytics tracking when:
 * - User logs in or changes (identity change)
 * - User switches tenants (tenant change)
 * - User logs out
 *
 * It properly handles internal users vs regular users by:
 * - Always using `setInternalUser` for internal users (never `setUser` with manual flags)
 * - Using `setUser` for regular users
 *
 * @param user - Current user session data or null if not authenticated
 * @param isLoading - Whether the user session is currently loading
 *
 * @example
 * ```tsx
 * const { data: user, isLoading } = useMe();
 * useAuthAnalytics(user, isLoading);
 * ```
 */
export const useAuthAnalytics = (
  user: UserSession | null | undefined,
  isLoading: boolean
): void => {
  const { setUser, setInternalUser } = useAnalytics();
  const prevUserRef = useRef<{
    id: string | null;
    tenantId: string | null;
    isAdmin: boolean;
  }>({
    id: null,
    tenantId: null,
    isAdmin: false,
  });

  useEffect(() => {
    // Reset on logout/loading
    if (isLoading || !user) {
      prevUserRef.current = { id: null, tenantId: null, isAdmin: false };
      return;
    }

    const current = {
      id: user.id,
      tenantId: resolveActiveTenantId(user),
      isAdmin: user.is_admin ?? false,
    };
    const prev = prevUserRef.current;

    const userProperties = {
      id: current.id,
      saas_tenant_id: current.tenantId,
    };

    // User identity changed (new login or different user) - configure destinations
    if (prev.id === null || prev.id !== current.id) {
      if (current.isAdmin) {
        // Configure destinations for internal user
        // setInternalUser has guards to prevent re-processing if already configured
        setInternalUser(userProperties);
      } else {
        // Regular user - just set properties
        setUser(userProperties);
      }
      prevUserRef.current = current;
      return;
    }

    // Same user, tenant changed - update properties
    // For internal users, always use setInternalUser (never setUser with manual flags)
    if (prev.tenantId !== null && prev.tenantId !== current.tenantId) {
      if (current.isAdmin) {
        // Internal user - always use setInternalUser to maintain proper destination configuration
        setInternalUser(userProperties);
      } else {
        // Regular user - just update properties
        setUser(userProperties);
      }
      prevUserRef.current = current;
    }
    // If nothing changed, refs are already up to date, no analytics call needed
  }, [user, isLoading, setUser, setInternalUser]);
};
